/**
 * The monthly supplier scorecard email (CR-7067).
 *
 * Once a month each supplier is emailed a PDF of their own performance
 * scorecard — the same figures they see on the Dashboard's Performance tab,
 * frozen at capture so the number in the PDF can be stood behind months later.
 *
 * Two things the copy says out loud, because both surprise people:
 *
 * * **One email per customer, not per supplier.** A supplier trading with three
 *   customers receives three, each holding only that customer's figures. They
 *   cannot be bundled: a supplier must never see one customer's data in
 *   another's report.
 * * **Suppliers with no activity get nothing.** Most trading relationships are
 *   dormant in any given month, and a scorecard of zeros with an N/A rating is
 *   worse than silence.
 *
 * The send obeys the master email switch, quiet hours and each recipient's own
 * subscription, exactly like every other notification on this page — it goes
 * through the same queue.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Switch } from '../../../ui/switch';
import { AlertCircle, FileText, Loader2, MailX } from 'lucide-react';
import { MAX_SEND_DAY, useScorecardEmail } from './useScorecardEmail';

interface SupplierScorecardCardProps {
  canConfigure: boolean;
  /** The tenant's master email switch. Off means nothing here can send. */
  emailsEnabled: boolean;
}

/** `''` while the field is being cleared; anything outside 1-28 is refused. */
const asDay = (text: string): number | null => {
  if (!/^\d{0,2}$/.test(text)) return null;
  if (text === '') return 0;
  const value = Number(text);
  return value >= 1 && value <= MAX_SEND_DAY ? value : null;
};

const ordinal = (n: number): string => {
  if (n >= 11 && n <= 13) return `${n}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';
  return `${n}${suffix}`;
};

export function SupplierScorecardCard({
  canConfigure,
  emailsEnabled,
}: SupplierScorecardCardProps) {
  const scorecard = useScorecardEmail();
  const [dayText, setDayText] = useState('');

  // Keep the field in step with the server value, but never while the admin is
  // mid-edit — an in-flight save would otherwise snap the cursor.
  useEffect(() => {
    if (!scorecard.saving) setDayText(String(scorecard.day));
  }, [scorecard.day, scorecard.saving]);

  const disabled = !canConfigure || scorecard.saving || scorecard.loading;

  const commitDay = () => {
    const parsed = asDay(dayText);
    if (parsed === null || parsed === 0) {
      setDayText(String(scorecard.day));   // refuse it, restore what was saved
      return;
    }
    if (parsed !== scorecard.day) void scorecard.save({ day: parsed });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4 text-muted-foreground" />
          Monthly supplier scorecard
        </CardTitle>
        <CardDescription>
          Email every supplier a PDF of their own performance scorecard once a
          month — the same figures they see on the Performance tab, frozen for
          the month they cover.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {scorecard.error && (
          <p className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {scorecard.error}
          </p>
        )}

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label htmlFor="scorecard-enabled" className="text-sm font-medium">
              Send monthly scorecards
            </Label>
            <p className="text-sm text-muted-foreground">
              One email per supplier, per customer. A supplier trading with
              three of your entities gets three, each showing only that
              entity&rsquo;s figures.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {scorecard.loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <Switch
              id="scorecard-enabled"
              checked={scorecard.enabled}
              disabled={disabled}
              onCheckedChange={(checked) => void scorecard.save({ enabled: checked })}
            />
          </div>
        </div>

        {scorecard.enabled && (
          <>
            <div className="flex flex-wrap items-end gap-3 border-t pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="scorecard-day" className="text-sm font-medium">
                  Send on day
                </Label>
                <Input
                  id="scorecard-day"
                  className="w-24"
                  inputMode="numeric"
                  value={dayText}
                  disabled={disabled}
                  onChange={(e) => {
                    if (asDay(e.target.value) !== null) setDayText(e.target.value);
                  }}
                  onBlur={commitDay}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
              </div>
              <p className="pb-2 text-sm text-muted-foreground">
                of each month, covering the month before. Capped at{' '}
                {MAX_SEND_DAY} so the day exists in February. Currently the{' '}
                <strong>{ordinal(scorecard.day)}</strong>.
              </p>
            </div>

            <p className="flex items-start gap-2 rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
              <MailX className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Suppliers with no orders delivered or expected in the month are
                skipped — a scorecard of zeros is not sent.
              </span>
            </p>
          </>
        )}

        {scorecard.enabled && !emailsEnabled && (
          <p className="flex items-start gap-2 text-sm text-amber-600 dark:text-amber-500">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Notification emails are switched off for this tenant, so nothing
              will be sent until the Email switch on the Events tab is on.
            </span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
